import './PaymentDetailsPage.scss';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { Slide } from '@progress/kendo-react-animation';
import { useRecoilValueLoadable } from 'recoil';

import {
    AdjustmentGridView,
    AdjustmentSummary,
    Icon,
    InvoiceCard,
    PaymentDetailHeader,
    PaymentMultiDetailSidebar,
    PaymentDetailSidebar,
    SuspenseBoundary
} from 'components';
import ExportButton from 'components/Buttons/ExportButton/ExportButton';
import { AdjustmentTypeMap, PaymentCategories } from 'config';
import { AdjustmentType, DateRangeType } from 'enums';
import { usePaymentParams, useSelectedCategory } from 'hooks';
import { paymentSummaries, usePaymentDetails, useRefreshPaymentDetails } from 'state';
import {
    InvoiceSummary,
    PaymentDetail,
    PaymentDetailHeaderData,
    PaymentSummary
} from 'types';
import { formatfileNameDate } from 'functions';
import { faMoneyCheckDollar } from '@fortawesome/pro-regular-svg-icons'

export default function PaymentDetailsPage() {
    const { accountId, vendorId, paymentId } = usePaymentParams();
    const refresh = useRefreshPaymentDetails(accountId, vendorId, paymentId);

    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving payment details for payment ${paymentId}`}
            onReset={refresh}
        >
            <PaymentDetailsComponent accountId={accountId} vendorId={vendorId} paymentId={paymentId} />
        </SuspenseBoundary>
    );
}

const PaymentDetailsComponent = ({ accountId, vendorId, paymentId }: { accountId: string; vendorId: string; paymentId: string }) => {
    const paymentSummariesLoadable = useRecoilValueLoadable(paymentSummaries({ accountId, dateRange: DateRangeType.OneYear }));
    const paymentSummary = paymentSummariesLoadable.state === 'hasValue'
        ? (paymentSummariesLoadable.contents as PaymentSummary[]).find(summary => summary.paymentIdRef === paymentId)
        : undefined;
    const paymentDetailsRaw = usePaymentDetails(accountId, vendorId, paymentId);
    const paymentDetails: PaymentDetail[] = useMemo(() => paymentDetailsRaw.map(detail => ({
        ...detail,
        paymentDate: detail.paymentDate || paymentSummary?.paymentDate
    })), [paymentDetailsRaw, paymentSummary?.paymentDate]);

    const invoiceSummaries: InvoiceSummary[] = useMemo(() => {
        const grouped = _.groupBy(paymentDetails.filter(d => d.invoiceId), 'invoiceId');
        
        return Object.entries(grouped).map(([invoiceId, details]) => {
            const first = details[0];
            const totalPriceAdj = _.sumBy(details, d => d.dotPriceAdjustment ?? 0);
            const totalQtyAdj = _.sumBy(details, d => d.dotQuantityAdjustment ?? 0);
            const totalInvoiced = _.sumBy(_.uniqBy(details, 'voucherLineNum'), d => d.voucherLineAmount ?? 0) || first.userVoucherDecimal || 0;
            
            return {
                ...first,
                accountId: first.accountId || accountId,
                vendorId: first.vendorId || vendorId,
                paymentId,
                invoiceId,
                userVoucherDecimal: totalInvoiced,
                voucherGrossAmount: totalInvoiced,
                dotPriceAdjustment: totalPriceAdj,
                dotQuantityAdjustment: totalQtyAdj,
                adjustmentTypeId: AdjustmentType.Invoice,
                adjustmentAmount: totalPriceAdj + totalQtyAdj,
                discountPaymentAmount: 0,
                invoicePaidAmount: totalInvoiced + totalPriceAdj + totalQtyAdj,
                selected: false,
            } as InvoiceSummary;
        }).filter(inv => inv.dotPriceAdjustment !== 0 || inv.dotQuantityAdjustment !== 0);
    }, [paymentDetails, accountId, vendorId, paymentId]);

    const [selectedDetails, setSelectedDetails] = useState<PaymentDetail[]>([])
    const [activeSelectedDetail, setActiveSelectedDetail] = useState<PaymentDetail>();
    const [expanded, setExpanded] = useState<boolean>(false);

    const clearSelectedDetails = (details: PaymentDetail[]) => {
        setSelectedDetails(details);
        if (!details.length) {
            setActiveSelectedDetail(undefined);
        }
    };

    const {
        gridProps,
        selectCategory,
        selectedCategory
    } = useSelectedCategory(paymentDetails, AdjustmentTypeMap, AdjustmentType.CustomerDeduction, () => clearSelectedDetails([]));

    const paymentDetailHeader: PaymentDetailHeaderData = useMemo(() => {
        if (_.isEmpty(paymentDetails))
            return {
                paymentIdRef: '',
                paymentDate: '',
                invoicedAmount: 0,
                paidAmount: 0,
                discAmount: 0,
                adjAmount: 0,
                dotPaymentStatus: '',
                vendorId: vendorId,
                vendorName: '',
            };

        const uniqueInvoiceRows = _.uniqBy(paymentDetails.filter(detail => detail.invoiceId), detail => detail.invoiceId);
        const paymentDate = paymentDetails.find(detail => detail.paymentDate)?.paymentDate || '';
        const paymentStatus = paymentDetails.find(detail => detail.statusDescription)?.statusDescription || '';
        const fallbackDiscountAmount = _.sumBy(uniqueInvoiceRows, detail => detail.discountPaymentAmount ?? 0);
        const fallbackAdjustmentAmount = _.sumBy(paymentDetails, detail => detail.adjustmentAmount ?? 0);

        return {
            paymentIdRef: paymentId || '',
            paymentDate: paymentSummary?.paymentDate || paymentDate,
            invoicedAmount: paymentSummary?.userVoucherDecimal ?? _.sumBy(uniqueInvoiceRows, detail => detail.userVoucherDecimal ?? 0),
            paidAmount: paymentSummary?.paymentAmount ?? paymentDetails[0]?.paymentAmount ?? 0,
            discAmount: paymentSummary?.discountPayAmount ?? fallbackDiscountAmount,
            adjAmount: paymentSummary
                ? (paymentSummary.dotAdjustmentTotalAmount ?? 0)
                    + (paymentSummary.dotDeductionAmount ?? 0)
                    + (paymentSummary.dotDeductionRepayAmount ?? 0)
                    + (paymentSummary.dotFuelAmount ?? 0)
                    + (paymentSummary.dotSupplierCreditAmount ?? 0)
                : fallbackAdjustmentAmount,
            dotPaymentStatus: paymentSummary?.dotPaymentStatus || paymentStatus,
            vendorId,
            vendorName: '',
        };
    }, [paymentDetails, paymentId, paymentSummary, vendorId]);

    
    const fileDate: string = formatfileNameDate(new Date());
    const exportFileName: string = `PaymentDetails_${paymentId}_${fileDate}.xlsx`
    const fieldsToExclude: string[] = ['marDetailRows'];
    const showDetails = () => selectedCategory === AdjustmentType.Invoice ? (
            invoiceSummaries && (invoiceSummaries.map(invoice => (
                <InvoiceCard
                    key={`${invoice.invoiceId}-${invoice.paymentIdRef}`}
                    invoice={invoice}
                />
            )))
        ) : <AdjustmentGridView
            key={selectedCategory}
            {...gridProps}
            selectedDetails={selectedDetails ?? []}
            setSelectedDetails={setSelectedDetails}
            setActiveSelectedDetail={(detail) => setActiveSelectedDetail(detail as PaymentDetail | undefined)}
            setExpanded={setExpanded}
            expandOnMultipleSelection
            hideMultiSelectDisputeButton
        />;

    return (
        <div className="details-page">
            <div className={'details-page-title'}>               
                <span>
                    <Icon icon={faMoneyCheckDollar}/> 
                </span>
                <span>
                    Payment Details
                </span>
            </div>
            <div className="details-page-main">
                <div className="details-page-main-header">
                    <PaymentDetailHeader
                        paymentDetailHeader={paymentDetailHeader}
                    />
                    <div className='details-page-main-header-export'>
                        <ExportButton data={paymentDetails} fileName={exportFileName} fieldsToExclude={fieldsToExclude} />
                    </div>
                </div>
                <div className="details-page-main-container">
                    <div className="details-page-main-container-summary">
                        <div className="details-page-main-container-summary-categories">
                            <AdjustmentSummary
                                adjustments={paymentDetails}
                                categories={PaymentCategories}
                                selectedCategory={selectedCategory}
                                selectCategory={selectCategory}
                            />
                        </div>
                    </div>
                    <div className="details-page-main-container-grid-container">
                        {showDetails()}
                    </div>
                </div>
            </div>
            <div>
                <Slide
                    appear={expanded}
                    className={`details-page-sidebar sidebar-${selectedDetails?.length ? "selected" : "unselected"}`}
                    direction={"left"}
                >
                    {expanded && selectedDetails?.length === 1 ?
                        <div
                            className={`details-page-sidebar-content`}
                        >
                            <PaymentDetailSidebar
                                selectedDetail={(selectedDetails && selectedDetails[0])}
                                setSelectedDetails={clearSelectedDetails}
                                expanded={expanded}
                                setExpanded={setExpanded}
                            />
                        </div>
                        : expanded && selectedDetails?.length > 1 ?
                        <div
                            className={`details-page-sidebar-content`}
                        >
                            <PaymentMultiDetailSidebar
                                activeDetail={activeSelectedDetail}
                                expanded={expanded}
                                selectedDetails={selectedDetails}
                                setActiveSelectedDetail={setActiveSelectedDetail}
                                setExpanded={setExpanded}
                                setSelectedDetails={clearSelectedDetails}
                            />
                        </div>
                        : null
                    }
                </Slide>
            </div>
        </div>
    )
};
